import { useMutation } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import type { DataCollectionResponse } from "@/api";
import { getDataSourceQueryKey, listDataSourcesQueryKey, schemas, updateDataSourceMutation } from "@/api";
import { EntityFormCard, formText, IdentityFields } from "@/components/build/EntityForm";
import { useEntitySave } from "@/components/build/useEntitySave";
import { useAppForm } from "@/components/form/form";
import { Text } from "@/components/Text";

const update = schemas.zDataCollectionUpdate.shape;
const overviewSchema = z.object({
  name: update.name.unwrap().unwrap(),
  description: update.description,
});
type OverviewValues = z.infer<typeof overviewSchema>;

/** Overview tab: name/description only. Access (visibility + team) lives on the Access tab. */
export function DataSourceOverviewForm({ source }: Readonly<{ source: DataCollectionResponse }>) {
  const { t } = useTranslation("dataSources");
  const save = useMutation(updateDataSourceMutation());
  const text = formText(t);
  const { saveExisting } = useEntitySave(text.messages);

  const form = useAppForm({
    defaultValues: { name: source.name, description: source.description ?? "" } as OverviewValues,
    validators: { onChange: overviewSchema },
    onSubmit: ({ value }) =>
      saveExisting({
        request: () =>
          save.mutateAsync({
            path: { collection_uuid: source.uuid },
            body: { name: value.name, description: value.description || null },
            throwOnError: true,
          }),
        invalidate: [listDataSourcesQueryKey(), getDataSourceQueryKey({ path: { collection_uuid: source.uuid } })],
        onSaved: () => form.reset(value),
      }),
  });

  return (
    <EntityFormCard label={t("form.label")} onSubmit={() => void form.handleSubmit()}>
      <IdentityFields form={form} fields={{ name: "name", description: "description" }} labels={text.labels} />
      <Text variant="caption" tone="tertiary">
        {t("access.manageNote")}
      </Text>
      <form.AppForm>
        <form.SubmitActions onCancel={() => form.reset()} />
      </form.AppForm>
    </EntityFormCard>
  );
}
